import { Helmet } from 'react-helmet-async';

// Default SEO configuration
const defaultSEO = {
  siteName: "LëtzServices",
  siteUrl: "https://letzservices.lu",
  defaultTitle: "LëtzServices - Services et Marketplace au Luxembourg",
  defaultDescription: "Trouvez ou proposez des services entre particuliers au Luxembourg. Ménage, bricolage, déménagement, cours et plus. 100% gratuit et local.",
  defaultImage: "/icons/icon-512x512.png",
  twitterHandle: "@letzservices",
  locale: "fr_LU"
};

// SEO Component for pages
export const SEO = ({ 
  title, 
  description, 
  image, 
  url, 
  type = "website",
  article = null,
  product = null,
  service = null,
  noindex = false 
}) => {
  const fullTitle = title 
    ? `${title} | ${defaultSEO.siteName}` 
    : defaultSEO.defaultTitle;
  
  const fullDescription = description || defaultSEO.defaultDescription;
  const fullImage = image 
    ? (image.startsWith('http') ? image : `${defaultSEO.siteUrl}${image}`)
    : `${defaultSEO.siteUrl}${defaultSEO.defaultImage}`;
  const fullUrl = url 
    ? `${defaultSEO.siteUrl}${url}` 
    : defaultSEO.siteUrl;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <link rel="canonical" href={fullUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={defaultSEO.siteName} />
      <meta property="og:locale" content={defaultSEO.locale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={fullImage} />

      {/* Article specific (for blog posts, news) */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:author" content={article.author} />
        </>
      )}
    </Helmet>
  );
};

// Structured Data for LocalBusiness
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "LëtzServices",
    "description": "Plateforme de services et marketplace entre particuliers au Luxembourg",
    "url": "https://letzservices.lu",
    "logo": "https://letzservices.lu/icons/icon-512x512.png",
    "image": "https://letzservices.lu/icons/icon-512x512.png",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Luxembourg",
      "addressCountry": "LU"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "49.6116",
      "longitude": "6.1319"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Luxembourg"
    },
    "priceRange": "€",
    "sameAs": [
      "https://facebook.com/letzservices",
      "https://instagram.com/letzservices"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Structured Data for a Service Post
export const ServiceSchema = ({ service }) => {
  if (!service) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "description": service.description,
    "provider": {
      "@type": "Person",
      "name": service.user_name
    },
    "areaServed": {
      "@type": "City",
      "name": service.ville
    },
    "offers": service.price ? {
      "@type": "Offer",
      "price": service.price.replace(/[^0-9.,]/g, ''),
      "priceCurrency": "EUR"
    } : undefined,
    "url": `https://letzservices.lu/posts/${service.slug || service.post_id}`
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Structured Data for a Product
export const ProductSchema = ({ product }) => {
  if (!product) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.title,
    "description": product.description,
    "image": product.images?.[0] || "https://letzservices.lu/icons/icon-512x512.png",
    "offers": {
      "@type": "Offer",
      "price": product.price?.replace(/[^0-9.,]/g, '') || "0",
      "priceCurrency": "EUR",
      "availability": product.status === "available" 
        ? "https://schema.org/InStock" 
        : "https://schema.org/SoldOut",
      "itemCondition": product.condition === "neuf" 
        ? "https://schema.org/NewCondition" 
        : "https://schema.org/UsedCondition"
    },
    "seller": {
      "@type": "Person",
      "name": product.user_name
    },
    "url": `https://letzservices.lu/marketplace/${product.slug || product.product_id}`
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Structured Data for Reviews/Ratings
export const ReviewSchema = ({ reviews, itemName, itemType = "LocalBusiness" }) => {
  if (!reviews || reviews.length === 0) return null;

  const avgRating = reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length;

  const schema = {
    "@context": "https://schema.org",
    "@type": itemType,
    "name": itemName,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": avgRating.toFixed(1),
      "reviewCount": reviews.length,
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": reviews.slice(0, 5).map(review => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": review.reviewer_name
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating,
        "bestRating": "5",
        "worstRating": "1"
      },
      "reviewBody": review.comment,
      "datePublished": review.created_at
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Breadcrumb Schema
export const BreadcrumbSchema = ({ items }) => {
  if (!items || items.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://letzservices.lu${item.url}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// FAQ Schema (good for landing pages)
export const FAQSchema = ({ faqs }) => {
  if (!faqs || faqs.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default SEO;
